import React, { useContext, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Calendar,
  User,
  FileText,
  Edit,
  Save,
  X,
  Trash2,
  Send,
} from "lucide-react";
import { AIEvents, AISummaryData, sourcesApi } from "@/api/source";
import { caseEventApi, CaseEvent } from "@/api/events";
import { useTranslation } from "react-i18next";
import { AuthContext, AuthContextType } from "@/context/AuthContext";
import { toast } from "@/components/ui/use-toast";

// Timeline Event Component
export const TimelineEvent: React.FC<{
  event: AIEvents;
  index: number;
  onUpdate: (index: number, updatedEvent: AIEvents) => void;
  onDelete: (index: number) => void;
}> = ({ event, index, onUpdate, onDelete }) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editedEvent, setEditedEvent] = useState<AIEvents>(event);

  useEffect(() => {
    setEditedEvent(event);
  }, [event]);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      // Check if the date is valid (not Invalid Date)
      if (isNaN(date.getTime())) {
        return null;
      }
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return null;
    }
  };

  const formatPageRange = (
    start: number | string | null,
    end: number | string | null,
  ) => {
    if (start && end) {
      return start === end
        ? t("ai_generated_events.label.page_single", { page: start })
        : t("ai_generated_events.label.page_range", { start, end });
    } else if (start) {
      return t("ai_generated_events.label.page_single", { page: start });
    } else if (end) {
      return t("ai_generated_events.label.page_single", { page: end });
    }
    return t("ai_generated_events.label.no_page_info");
  };

  const handleSave = () => {
    onUpdate(index, editedEvent);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedEvent(event);
    setIsEditing(false);
  };

  const handleDelete = () => {
    onDelete(index);
  };

  const handleInputChange = (field: keyof AIEvents, value: string | number) => {
    setEditedEvent((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const formattedDate = formatDate(event.date);

  return (
    <div className="relative flex gap-4 pb-6">
      {/* Timeline line */}
      <div className="absolute left-6 top-6 w-0.5 h-full bg-gray-200" />

      {/* Timeline dot */}
      <div className="relative z-10 flex items-center justify-center w-12 h-12 bg-primary rounded-full text-white font-semibold text-sm">
        {index + 1}
      </div>

      {/* Event content */}
      <div className="flex-1 bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <div className="flex items-start justify-between mb-2">
          {isEditing ? (
            <div className="flex-1 space-y-3">
              <Input
                value={editedEvent.title || ""}
                onChange={(e) => handleInputChange("title", e.target.value)}
                placeholder={t("ai_generated_events.label.title")}
                className="text-lg font-semibold"
              />
              <Textarea
                value={editedEvent.description || ""}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                placeholder={t("ai_generated_events.label.description")}
                className="min-h-[80px]"
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={editedEvent.author || ""}
                  onChange={(e) => handleInputChange("author", e.target.value)}
                  placeholder={t("ai_generated_events.label.author")}
                />
                <Input
                  type="date"
                  value={
                    editedEvent.date
                      ? new Date(editedEvent.date).toISOString().split("T")[0]
                      : ""
                  }
                  onChange={(e) => handleInputChange("date", e.target.value)}
                  placeholder={t("ai_generated_events.label.date")}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="number"
                  value={editedEvent.starting_page || ""}
                  onChange={(e) =>
                    handleInputChange(
                      "starting_page",
                      e.target.value ? parseInt(e.target.value) : "",
                    )
                  }
                  placeholder={t("ai_generated_events.label.start_page")}
                />
                <Input
                  type="number"
                  value={editedEvent.ending_page || ""}
                  onChange={(e) =>
                    handleInputChange(
                      "ending_page",
                      e.target.value ? parseInt(e.target.value) : "",
                    )
                  }
                  placeholder={t("ai_generated_events.label.end_page")}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleSave}
                  size="sm"
                  className="flex items-center gap-1"
                >
                  <Save className="w-3 h-3" />
                  {t("common.button.save")}
                </Button>
                <Button
                  onClick={handleCancel}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1"
                >
                  <X className="w-3 h-3" />
                  {t("common.button.cancel")}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <h4 className="font-semibold text-gray-900 text-lg">
                {event.title}
              </h4>
              <div className="flex gap-2">
                {formattedDate && (
                  <Badge variant="outline" className="text-xs">
                    <Calendar className="w-3 h-3 mr-1" />
                    {formattedDate}
                  </Badge>
                )}
                <Button
                  onClick={() => setIsEditing(true)}
                  variant="ghost"
                  size="sm"
                  className="flex items-center gap-1"
                >
                  <Edit className="w-3 h-3" />
                  {t("common.button.edit")}
                </Button>
                <Button
                  onClick={handleDelete}
                  variant="ghost"
                  size="sm"
                  className="flex items-center gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-3 h-3" />
                  {t("common.button.remove")}
                </Button>
              </div>
            </>
          )}
        </div>

        {!isEditing && (
          <>
            <p className="text-gray-600 mb-3 leading-relaxed">
              {event.description}
            </p>

            <div className="flex items-center gap-4 text-sm text-gray-500">
              <div className="flex items-center gap-1">
                <User className="w-4 h-4" />
                <span>
                  {event.author || t("ai_generated_events.label.unknown")}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <FileText className="w-4 h-4" />
                <span>
                  {formatPageRange(event.starting_page, event.ending_page)}
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Timeline Component
export const AITimeline: React.FC<{
  events: AIEvents[];
  sourceId?: string;
  caseId?: number;
  aiSummaryData: AISummaryData;
}> = ({ events, sourceId, caseId, aiSummaryData }) => {
  const { values: contextValues } = useContext(AuthContext) as AuthContextType;

  const { user } = contextValues;
  function sortEvents(eventsInput: AIEvents[]) {
    return eventsInput.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA.getTime() - dateB.getTime();
    });
  }

  const { t } = useTranslation();
  const [editedEvents, setEditedEvents] = useState<AIEvents[]>(
    sortEvents(events),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  useEffect(() => {
    setEditedEvents(sortEvents(events));
  }, [events]);

  const handleEventUpdate = (index: number, updatedEvent: AIEvents) => {
    setEditedEvents((prev) => {
      const newEvents = [...prev];
      newEvents[index] = updatedEvent;
      return sortEvents(newEvents);
    });
  };

  const handleEventDelete = (index: number) => {
    setEditedEvents((prev) => {
      const newEvents = [...prev];
      newEvents.splice(index, 1);
      return sortEvents(newEvents);
    });
  };

  const handleSaveAsDraft = async () => {
    if (!sourceId) {
      console.error("Source ID is required to save draft");
      return;
    }

    setIsSavingDraft(true);
    try {
      // Save the AI summary data with the edited events as draft
      const updatedAiSummaryData: AISummaryData = {
        ...aiSummaryData,
        result: editedEvents, // Update the result with edited events
        summary: {
          ...aiSummaryData.summary,
          final_unique: editedEvents.length, // Update the count
        },
      };

      await sourcesApi.saveAiSummaryData({
        sourceId: Number(sourceId),
        token: user?.access,
        data: {
          ai_summary_data: updatedAiSummaryData,
        },
      });

      toast({
        title: t("ai_generated_events.toast.draft_saved_successfully"),
        description: t("ai_generated_events.toast.draft_saved_description", {
          count: editedEvents.length,
        }),
      });
    } catch (error) {
      console.error("Error saving draft:", error);
      toast({
        title: t("ai_generated_events.toast.failed_to_save_draft"),
        variant: "destructive",
      });
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handlePublishToCaseEvents = async () => {
    if (!sourceId || !caseId) {
      console.error("Source ID and Case ID are required to publish events");
      return;
    }

    setIsSaving(true);
    try {
      // Step 1: Save the AI summary data with the edited events
      const updatedAiSummaryData: AISummaryData = {
        ...aiSummaryData,
        result: editedEvents, // Update the result with edited events
        summary: {
          ...aiSummaryData.summary,
          final_unique: editedEvents.length, // Update the count
        },
      };

      await sourcesApi.saveAiSummaryData({
        sourceId: Number(sourceId),
        token: user?.access,
        data: {
          ai_summary_data: updatedAiSummaryData,
        },
      });

      // Step 2: Create case events from the edited events
      const promises = editedEvents.map(async (event) => {
        const caseEvent: Partial<CaseEvent> = {
          title: event.title,
          author: event.author,
          date: event.date
            ? new Date(event.date).toLocaleDateString("sv")
            : new Date().toLocaleDateString("sv"),
          description: event.description,
          starting_page: event.starting_page?.toString() || null,
          ending_page: event.ending_page?.toString() || null,
          include_in_timeline: true,
          source_id: sourceId,
          case_tag_ids: [],
        };

        return caseEventApi.create({
          url: "",
          data: caseEvent,
          token: user?.access,
        });
      });

      await Promise.all(promises);
      toast({
        title: t("ai_generated_events.toast.events_published_successfully"),
        description: t(
          "ai_generated_events.toast.events_published_description",
          {
            count: editedEvents.length,
          },
        ),
      });
    } catch (error) {
      console.error("Error publishing events to case events:", error);
      toast({
        title: t("ai_generated_events.toast.failed_to_publish_events"),
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!editedEvents || editedEvents.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <p>{t("ai_generated_events.label.no_events_found")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-sm">
          {t("ai_generated_events.label.ai_generated_timeline")}
        </h4>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs">
            {editedEvents.length}{" "}
            {editedEvents.length !== 1
              ? t("ai_generated_events.statictext.events")
              : t("ai_generated_events.statictext.event")}
          </Badge>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleSaveAsDraft}
              disabled={isSavingDraft || !sourceId || !user?.access}
              variant="default"
              size="sm"
              className="flex items-center gap-1"
            >
              <Save className="w-4 h-4" />
              {isSavingDraft
                ? t("common.button.loading")
                : t("ai_generated_events.button.save_as_draft")}
            </Button>
            <Button
              onClick={handlePublishToCaseEvents}
              disabled={isSaving || !sourceId || !caseId || !user?.access}
              size="sm"
              className="flex items-center gap-1"
            >
              <Send className="w-4 h-4" />
              {isSaving
                ? t("common.button.loading")
                : t("ai_generated_events.button.publish_to_case_events")}
            </Button>
          </div>
        </div>
      </div>

      <div className="relative">
        {editedEvents.map((event, index) => (
          <TimelineEvent
            key={index}
            event={event}
            index={index}
            onUpdate={handleEventUpdate}
            onDelete={handleEventDelete}
          />
        ))}
      </div>
    </div>
  );
};
